import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { faList, faBorderAll } from '@fortawesome/free-solid-svg-icons';

export type ViewMode = 'table' | 'cards';

/**
 * Small segmented control to flip a list area between a table and cards.
 *
 * Resolution order for the initial value: a previously-saved choice in
 * localStorage (per storageKey), then the bound [value], then a responsive
 * default (cards on narrow screens). The resolved value is emitted once on
 * init so the host always reflects what the user will actually see.
 */
@Component({
  selector: 'kw-view-toggle',
  template: `
    <div class="kw-view-toggle" role="group" aria-label="View mode">
      <button type="button" [class.on]="value === 'table'" (click)="set('table')"
        title="Table view" [attr.aria-pressed]="value === 'table'">
        <fa-icon [icon]="faList"></fa-icon><span class="lbl">Table</span>
      </button>
      <button type="button" [class.on]="value === 'cards'" (click)="set('cards')"
        title="Card view" [attr.aria-pressed]="value === 'cards'">
        <fa-icon [icon]="faCards"></fa-icon><span class="lbl">Cards</span>
      </button>
    </div>
  `,
  styles: [`
    .kw-view-toggle { display: inline-flex; border: 1px solid #e4e9ef; border-radius: 8px; overflow: hidden; background: #fff; }
    .kw-view-toggle button { border: none; background: none; padding: 7px 12px; font-size: 13px; color: #64748b;
      display: inline-flex; align-items: center; gap: 6px; font-weight: 600; border-right: 1px solid #e4e9ef; cursor: pointer; }
    .kw-view-toggle button:last-child { border-right: none; }
    .kw-view-toggle button:hover { background: #f5f7f9; color: #17212e; }
    .kw-view-toggle button.on { background: #253d5b; color: #fff; }
    @media (max-width: 620px) { .kw-view-toggle .lbl { display: none; } }
  `]
})
export class KwViewToggleComponent implements OnInit {
  @Input() storageKey: string;
  @Input() value: ViewMode;
  @Output() valueChange = new EventEmitter<ViewMode>();

  faList = faList;
  faCards = faBorderAll;

  constructor(private breakpoints: BreakpointObserver) {}

  ngOnInit(): void {
    const saved = this.storageKey ? localStorage.getItem(this.storageKey) : null;
    let resolved: ViewMode;
    if (saved === 'table' || saved === 'cards') {
      resolved = saved;
    } else if (this.value) {
      resolved = this.value;
    } else {
      resolved = this.breakpoints.isMatched('(max-width: 760px)') ? 'cards' : 'table';
    }
    this.value = resolved;
    // Defer so the host's binding update doesn't trip ExpressionChanged checks.
    Promise.resolve().then(() => this.valueChange.emit(resolved));
  }

  set(mode: ViewMode): void {
    if (this.value === mode) {
      return;
    }
    this.value = mode;
    if (this.storageKey) {
      localStorage.setItem(this.storageKey, mode);
    }
    this.valueChange.emit(mode);
  }
}
